import type { ReactNode } from "react";
import { Lock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatureAccess } from "@/hooks/useFeature";
import { usePlans } from "@/hooks/usePlans";
import { minPlanForFeature } from "@/lib/featurePlan";
import LockedSkeleton, { type SkeletonPreset } from "@/components/shared/LockedSkeleton";
import type { ModuleFeatures } from "@/store/authStore";

const PRESET_BY_FEATURE: Record<string, SkeletonPreset> = {
  analytics: "chart",
  advanced_analytics: "chart",
  reports: "list",
  advanced_reports: "list",
  marketing: "cards",
  integrations: "cards",
  online_shop: "grid",
  online_orders: "list",
};

interface FeatureGateProps {
  feature: keyof ModuleFeatures;
  children: ReactNode;
  /** "page" = full-screen lock (default). "compact" = in-widget lock. */
  variant?: "page" | "compact";
  /** Back-compat alias for variant="compact". */
  inline?: boolean;
  /** Optional display-name override; defaults to the localized feature_names entry. */
  featureLabel?: string;
  /** Optional custom backdrop; defaults to a preset skeleton. */
  skeleton?: ReactNode;
}

export default function FeatureGate({
  feature,
  children,
  variant,
  inline,
  featureLabel,
  skeleton,
}: FeatureGateProps) {
  const access = useFeatureAccess(feature);
  const { t } = useTranslation();
  const { data: plans } = usePlans();

  // Allowed → render the real feature. Otherwise never mount children (no gated API calls).
  if (access === "allowed") return <>{children}</>;

  // "restricted" = the plan includes the feature but the owner hid it from this
  // user's role (a permission block, not a paywall) → no plan badge, no upgrade CTA.
  const restricted = access === "restricted";
  const key = String(feature);
  const name = featureLabel ?? t(`feature_names.${key}`, key.replace(/_/g, " "));
  const plan = restricted ? null : minPlanForFeature(key, plans);
  const resolvedVariant = variant ?? (inline ? "compact" : "page");
  const preset = PRESET_BY_FEATURE[key] ?? "list";
  const backdrop = skeleton ?? <LockedSkeleton preset={preset} />;

  const title = restricted
    ? t("feature_gate.restricted_title", "Access restricted")
    : t("feature_gate.locked_title", "{{feature}} is locked", { feature: name });
  const description = restricted
    ? t("feature_gate.restricted_description", "This section is hidden by the shop owner. Contact your administrator.")
    : t("feature_gate.locked_description", "Upgrade your plan to unlock this feature.");
  const cta = t("feature_gate.cta", "Upgrade plan");
  const iconWrap = restricted ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-500";

  if (resolvedVariant === "compact") {
    return (
      <div className="relative min-h-[140px] w-full overflow-hidden rounded-xl border border-ds-ceramic">
        <div className="absolute inset-0 opacity-50 blur-[3px] pointer-events-none">{backdrop}</div>
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 p-4 text-center">
          <div className={`mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg ${iconWrap}`}>
            <Lock className="h-4 w-4" />
          </div>
          <p className="text-xs font-semibold text-ds-house">{title}</p>
          {!restricted && (
            <Link
              to="/subscription"
              className="mt-1 inline-flex items-center gap-0.5 text-xs font-semibold text-ds-accent"
            >
              {cta}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[60vh] w-full overflow-hidden">
      <div className="absolute inset-0 opacity-50 blur-[4px] pointer-events-none">{backdrop}</div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-ds-warm/60" />
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-2xl border border-ds-ceramic bg-white/90 px-6 py-9 text-center shadow-ds-lg backdrop-blur">
          <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-xl ${iconWrap}`}>
            <Lock className="h-6 w-6" />
          </div>
          {plan && (
            <span className="mb-2 inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800">
              {t("feature_gate.badge", "Available on {{plan}}", { plan: plan.name })}
            </span>
          )}
          <h2 className="mb-1.5 text-xl font-bold text-ds-house" style={{ letterSpacing: "-0.01em" }}>
            {title}
          </h2>
          <p className={`text-sm leading-relaxed text-muted-foreground ${restricted ? "mb-0" : "mb-6"}`}>
            {description}
          </p>
          {!restricted && (
            <Link
              to="/subscription"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ds-accent px-5 py-3 text-sm font-semibold text-white shadow-ds transition-all hover:bg-ds-green active:scale-95"
            >
              {cta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
